import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  Checkbox,
  TextField,
  IconButton,
  Button,
  Typography,
  Grid,
  Popper,
  Chip,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { baseUrl } from "../config/api-config";
import useErrorHandler from "../hooks/useErrorHandler";
import { RotateRight, Close } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import useSnackbar from "../hooks/useSnackbar";

const useStyles = makeStyles(() => ({
  selectedItemsContainer: {
    minHeight: "200px",
    maxHeight: "100px",
    overflowY: "auto",
    border: "1px solid #ccc",
    borderRadius: "4px",
    marginTop: "10px",
    padding: "5px",
  },
  container: {
    maxHeight: "200px",
    overflowY: "auto",
  },
  option: {
    fontSize: "12px",
  },

  selectedItem: {
    display: "flex",
    alignItems: "flex-start", // Change this to 'flex-start'
    marginBottom: "5px",
    marginRight: "10px", // Add some margin between items
  },
  removeButton: {
    marginLeft: "5px",
  },
  // ... (Other styles) ...
}));

const MultiSelectDropdown = ({
  prefilledColumnValues,
  prefilledLabelValues,
  tableName,
  onValueChange,
}) => {
  //remove the duplicates
  const uniqueColumnArray = [...new Set(prefilledColumnValues)];
  const uniqueLabelArray = [...new Set(prefilledLabelValues)];

  const [options, setOptions] = useState([]);
  const [values, setValues] = useState([]);
  const [labelValues, setLabelValues] = useState([]);
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const { data: user } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const errorHandler = useErrorHandler();
  const [tableValue, setTableValue] = useState(tableName);
  const limit = 10;
  const snackbar = useSnackbar();
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (Array.isArray(uniqueColumnArray)) {
      // Combine the uniqueColumnArray and uniqueLabelArray into the selectedItems state
      setSelectedItems(
        uniqueColumnArray.map((column_name, index) => ({
          column_name,
          label_name: uniqueLabelArray[index] || "", // Set blank label name if there's no corresponding label
        }))
      );
      setValues(convertArrayToObject(uniqueColumnArray));
      setLabelValues(convertArrayToObject(uniqueLabelArray));
    } else {
      setValues([]);
    }
  }, []);

  useEffect(() => {
    if (tableName) {
      setTableValue(tableName);
      fetchOptions();
    }
    return;
  }, [offset, inputValue, tableValue, tableName]);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/getColumnsListbyView?limit=${limit}&offset=${offset}&view_name=${tableValue}&col_name=${inputValue}`,
        {
          headers: {
            authorization: user.token,
          },
        }
      );
      const data = await response.json();
      setOptions((prevOptions) => {
        if (offset === 0) {
          return [...data.data.ColumnsList];
        } else {
          return [...prevOptions, ...data.data.ColumnsList];
        }
      });
      setLoading(false);
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleSelect = (event, selectedOptions) => {
    const lastSelected = selectedOptions[selectedOptions.length - 1];
    const isLastSelectedInValues = values.some(
      (value) => value.column_name === lastSelected.column_name
    );

    // Step 2: Add the selected column name with a blank label name to the state
    if (!isLastSelectedInValues) {
      // Update the selectedItems state with the new item and pass it to onValueChange directly
      setSelectedItems((prevSelectedItems) => {
        const newSelectedItems = [
          ...prevSelectedItems,
          {
            column_name: lastSelected.column_name,
            label_name: lastSelected.column_name,
          },
        ];

        onValueChange(newSelectedItems); // Pass the updated state to the function
        return newSelectedItems; // Return the updated state to setSelectedItems
      });

      // Assuming you want to set the values state with the selectedOptions array
      setValues(selectedOptions);

      // Clear the input value when an option is selected
      setInputValue("");
    } else {
      snackbar.showError("Column already exists in the selected list.");
    }
  };

  const handleRemove = (value) => {
    // Step 1: Remove the item from 'values' state
    const updatedValues = values.filter(
      (v) => v.column_name !== value.column_name
    );
    setValues(updatedValues);

    // Step 2: Remove the item from 'selectedItems' state
    const updatedItems = selectedItems.filter(
      (item) => item.column_name !== value.column_name
    );
    setSelectedItems(updatedItems);

    onValueChange(updatedItems); // Update the parent component if needed
  };
  const handlePageChange = async () => {
    setOffset((prev) => prev + 10);
  };

  useEffect(() => {
    if (inputValue === "") {
      setOptions([]);
      setOpen(true);
    }
  }, [inputValue]);

  const handleBlur = () => {
    setOpen(false);
  };

  const handleFocus = () => {
    setOpen(true);
  };
  const handleEdit = (valueToEdit, editedValue) => {
    // Update the 'selectedItems' state directly to reflect the changes in the TextField
    const updatedItems = selectedItems.map((item) =>
      item.column_name === valueToEdit.column_name
        ? { ...item, label_name: editedValue }
        : item
    );

    setSelectedItems(updatedItems); // Update the 'selectedItems' state
    onValueChange(updatedItems);
    // (If needed, you can also update 'values' state here based on 'updatedItems')
  };

  const renderInput = (params) => (
    <div style={{ marginTop: "10px" }}>
      <TextField
        {...params}
        label="Column Name"
        variant="outlined"
        onChange={(event) => {
          setOffset(0);
          setOptions([]);
          setInputValue(event.target.value);
        }}
        value={inputValue}
        InputProps={{
          style: { fontSize: 12 },
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
              <Button
                className="quote-load-more-button"
                onClick={handlePageChange}
                style={{
                  maxWidth: "30px",
                  maxHeight: "42px",
                  minWidth: "30px",
                  minHeight: "30px",
                }}
              >
                <RotateRight />
              </Button>
            </>
          ),
        }}
        autoFocus
      />
    </div>
  );

  const classes = useStyles();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <div className={classes.container}>
          <Autocomplete
            open={open}
            classes={{ option: classes.option }}
            fullWidth
            multiple
            options={options}
            getOptionLabel={(option) => option.column_name}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  checked={selected}
                  sx={{ mr: 1 }}
                  size="small"
                  color="primary"
                />
                {option.column_name}
              </li>
            )}
            renderTags={() => null}
            renderInput={renderInput}
            value={values}
            onChange={handleSelect}
            onBlur={handleBlur}
            onFocus={handleFocus}
            PopperComponent={({ children, ...popperProps }) => (
              <Popper
                {...popperProps}
                placement="bottom-start"
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {children}
              </Popper>
            )}
          />
        </div>
      </Grid>
      <Grid item xs={12} sm={6}>
        <div
          className={classes.selectedItemsContainer}
          style={{ overflowX: "auto", width: "100%" }}
        >
          <Typography variant="body2">{"Selected column names:"}</Typography>

          {selectedItems.map((item, index) => (
            <div
              key={item.column_name}
              className={classes.selectedItem}
              style={{
                display: "flex",
                flexDirection: "column",
                marginBottom: "5px",
              }}
            >
              <Chip
                sx={{
                  height: "auto",
                  "& .MuiChip-label": {
                    display: "block",
                    whiteSpace: "normal",
                  },
                }}
                color="primary"
                label={item.column_name}
                onDelete={() => handleRemove(item)}
              />
              <TextField
                label="Label Name"
                sx={{ mt: "5px" }}
                size="small"
                value={item.label_name}
                onChange={(event) => handleEdit(item, event.target.value)}
                onBlur={handleBlur}
                onFocus={() => handleFocus(item)}
                autoFocus // Auto-focus on the input field when it is rendered
              />
            </div>
          ))}
        </div>
      </Grid>
    </Grid>
  );
};

export default MultiSelectDropdown;

function convertArrayToObject(arr) {
  return arr.map((value) => ({ column_name: value }));
}
